import { atom } from 'jotai'

import {
  maxLength,
  required,
  validateField
} from '@sequencehq/utils/validation'
import { IntegrationServices } from '@sequencehq/api'

import { formStateAtom } from './state.atoms'
import { secondaryOptionsVisibleAtom } from '../../common/atoms'
import {
  LINE_ITEM_DESCRIPTION_MAX_LENGTH,
  LINE_ITEM_NAME_MAX_LENGTH
} from '../../constants'

type ValidationResults = {
  title: string[]
  description: string[]
  taxCategoryId: string[]
  externalIds: Array<{
    key: IntegrationServices
    value: string[]
  }>
}

export const validationResultsAtom = atom<ValidationResults | null>(null)

export const validateFormAtom = atom(null, (get, set) => {
  const state = get(formStateAtom)

  const results = {
    title: validateField(state.title)([
      required,
      maxLength(
        LINE_ITEM_NAME_MAX_LENGTH,
        `Name must not exceed ${LINE_ITEM_NAME_MAX_LENGTH} characters`
      )
    ]),
    description: validateField(state.description)([
      maxLength(
        LINE_ITEM_DESCRIPTION_MAX_LENGTH,
        `Description must not exceed ${LINE_ITEM_DESCRIPTION_MAX_LENGTH} characters`
      )
    ]),
    taxCategoryId: validateField(state.taxCategoryId)([required]),
    externalIds: state.externalIds.map(externalId => ({
      key: externalId.key,
      value: validateField(externalId.value)([required])
    }))
  }

  const secondaryOptionsValid = isSecondaryOptionsValid(results)

  if (!secondaryOptionsValid) {
    set(secondaryOptionsVisibleAtom, true)
  }

  set(validationResultsAtom, results)
  return isFormValid(results)
})

const isSecondaryOptionsValid = (results: ValidationResults): boolean => {
  return results.externalIds.every(externalId => externalId.value.length === 0)
}

const isFormValid = (results: ValidationResults): boolean => {
  const { externalIds, ...baseFields } = results

  const baseFieldsValid = Object.values(baseFields).every(
    errors => errors.length === 0
  )

  const secondaryOptionsValid = isSecondaryOptionsValid(results)

  return [baseFieldsValid, secondaryOptionsValid].every(Boolean)
}
